import React, { useState, useEffect, useRef } from "react";
import {
	Box,
	Paper,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	IconButton,
	Button,
	Alert,
	Grid,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	CircularProgress,
	Chip,
} from "@mui/material";
import {
	Edit as EditIcon,
	Plus as PlusIcon,
	Trash2 as TrashIcon,
	Upload as UploadIcon,
	Video as VideoIcon,
	ExternalLink as LinkIcon,
	MessageSquare as QuizIcon,
	ToggleLeft as DeactivateIcon,
	ToggleRight as ActivateIcon,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import dayjs from "dayjs";

// Config object for different sections
const SECTIONS_CONFIG = {
	expense_types: {
		title: "Expense Types",
		fields: [{ name: "name", label: "Name", type: "text" }],
		hasStatusToggle: true,
	},
	visit_types: {
		title: "Visit Types",
		fields: [
			{ name: "name", label: "Name", type: "text" },
			{
				name: "visit_type",
				label: "Visit Type",
				type: "select",
				options: ["Service", "Sales"],
			},
			{ name: "start_date", label: "Start Date", type: "date" },
			{ name: "end_date", label: "End Date", type: "date" },
		],
		hasStatusToggle: true, // New property to indicate this section has activate/deactivate
	},
	visit_status: {
		title: "Visit Status",
		fields: [{ name: "name", label: "Name", type: "text" }],
	},
	school_types: {
		title: "School Types",
		fields: [{ name: "name", label: "Name", type: "text" }],
	},
	grades: {
		title: "Grades",
		fields: [
			{ name: "name", label: "Name", type: "text" },
			{
				name: "parent_grade",
				label: "Parent Grade",
				type: "select",
				options: [
					"Grade 1",
					"Grade 2",
					"Grade 3",
					"Grade 4",
					"Grade 5",
					"Grade 6",
					"Grade 7",
					"Grade 8",
					"Grade 9",
					"Grade 10",
					"Grade 11",
					"Grade 12",
				],
			},
		],
	},
	onboarding_videos: {
		title: "Onboarding Videos",
		fields: [
			{ name: "title", label: "Title", type: "text" },
			{ name: "description", label: "Description", type: "text" },
			{ name: "playorder", label: "Play Order", type: "number" },
			{ name: "video_link", label: "Video Link", type: "text", readonly: true },
			{ name: "thumbnail", label: "Thumbnail", type: "text", readonly: true },
		],
		hasFileUpload: true,
		hasThumbnailUpload: true,
	},
	// Updated section for video quizzes - removed display_time_percent field
	video_quizzes: {
		title: "Video Quizzes",
		fields: [
			{
				name: "video_id",
				label: "Video",
				type: "select",
				options: "onboarding_videos",
			},
			{ name: "question", label: "Question", type: "text" },
			{ name: "option_a", label: "Option A", type: "text" },
			{ name: "option_b", label: "Option B", type: "text" },
			{ name: "option_c", label: "Option C", type: "text" },
			{ name: "option_d", label: "Option D", type: "text" },
			{
				name: "correct_answer",
				label: "Correct Answer",
				type: "select",
				options: ["a", "b", "c", "d"],
			},
			{
				name: "quiz_order",
				label: "Quiz Order",
				type: "number",
				min: 1,
			},
		],
	},
	funmath_topics: {
		title: "Funmath Topics",
		fields: [
			{ name: "name", label: "Name", type: "text" },
			{ name: "sort_order", label: "Sort Order", type: "number", min: 0 },
		],
		hasStatusToggle: true,
	},
};

const DataSection = ({
	sectionKey,
	config,
	onAdd,
	onEdit,
	onDelete,
	onToggleStatus,
	data,
	onViewVideo,
}) => (
	<Paper
		elevation={0}
		sx={{
			p: 3,
			height: "100%",
			border: "1px solid",
			borderColor: "divider",
			borderRadius: 2,
		}}>
		<Box
			sx={{
				display: "flex",
				justifyContent: "space-between",
				alignItems: "center",
				mb: 3,
			}}>
			<Typography variant="h6">{config.title}</Typography>
			<Button
				variant="contained"
				startIcon={<PlusIcon size={20} />}
				onClick={() => onAdd(sectionKey)}>
				Add New
			</Button>
		</Box>

		<TableContainer>
			<Table>
				<TableHead>
					<TableRow>
						{sectionKey === "video_quizzes" ? (
							<>
								<TableCell>Video</TableCell>
								<TableCell>Question</TableCell>
								<TableCell>Quiz Order</TableCell>
							</>
						) : sectionKey === "grades" ? (
							<>
								<TableCell>Name</TableCell>
								<TableCell>Parent Grade</TableCell>
							</>
						) : sectionKey === "visit_types" ? (
							<>
								<TableCell>Name</TableCell>
								<TableCell>Visit Type</TableCell>
								<TableCell>Start Date</TableCell>
								<TableCell>End Date</TableCell>
								<TableCell>Status</TableCell>
							</>
						) : sectionKey === "funmath_topics" ? (
							<>
								<TableCell>Name</TableCell>
								<TableCell>Sort</TableCell>
								<TableCell>Status</TableCell>
							</>
						) : (
							<TableCell>Name</TableCell>
						)}
						{sectionKey === "onboarding_videos" && (
							<>
								<TableCell>Description</TableCell>
								<TableCell>Play Order</TableCell>
								<TableCell>Thumbnail</TableCell>
							</>
						)}
						<TableCell align="right">Actions</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{data[sectionKey]?.map((item) => (
						<TableRow key={item.id}>
							{sectionKey === "video_quizzes" ? (
								<>
									<TableCell>
										{data.onboarding_videos?.find((v) => v.id === item.video_id)
											?.title || "Unknown Video"}
									</TableCell>
									<TableCell>{item.question}</TableCell>
									<TableCell>{item.quiz_order}</TableCell>
								</>
							) : sectionKey === "grades" ? (
								<>
									<TableCell>{item.name}</TableCell>
									<TableCell>{item.parent_grade || "N/A"}</TableCell>
								</>
							) : sectionKey === "visit_types" ? (
								<>
									<TableCell>{item.name}</TableCell>
									<TableCell>{item.visit_type || "Service"}</TableCell>
									<TableCell>
										{item.start_date
											? dayjs(item.start_date).format("DD/MM/YYYY")
											: "—"}
									</TableCell>
									<TableCell>
										{item.end_date
											? dayjs(item.end_date).format("DD/MM/YYYY")
											: "—"}
									</TableCell>
									<TableCell>
										<Chip
											label={item.status ? "Active" : "Inactive"}
											color={item.status ? "success" : "default"}
											size="small"
										/>
									</TableCell>
								</>
							) : sectionKey === "funmath_topics" ? (
								<>
									<TableCell>{item.name}</TableCell>
									<TableCell>{item.sort_order ?? 0}</TableCell>
									<TableCell>
										<Chip
											label={item.status ? "Active" : "Inactive"}
											color={item.status ? "success" : "default"}
											size="small"
										/>
									</TableCell>
								</>
							) : (
								<TableCell>{item.name || item.title}</TableCell>
							)}
							{sectionKey === "onboarding_videos" && (
								<>
									<TableCell>{item.description}</TableCell>
									<TableCell>{item.playorder}</TableCell>
									<TableCell>
										{item.thumbnail ? (
											<img
												src={item.thumbnail}
												alt="Thumbnail"
												style={{ width: "50px", height: "auto" }}
											/>
										) : (
											<Typography variant="body2" color="text.secondary">
												No thumbnail
											</Typography>
										)}
									</TableCell>
								</>
							)}
							<TableCell align="right">
								{sectionKey === "onboarding_videos" && (
									<>
										{item.video_link && (
											<IconButton
												size="small"
												color="primary"
												onClick={() => onViewVideo(item.video_link)}
												sx={{ mr: 1 }}
												title="Preview Video">
												<VideoIcon size={16} />
											</IconButton>
										)}
										<IconButton
											size="small"
											color="secondary"
											onClick={() =>
												onAdd("video_quizzes", { video_id: item.id })
											}
											sx={{ mr: 1 }}
											title="Add Quiz">
											<QuizIcon size={16} />
										</IconButton>
									</>
								)}
								<IconButton
									size="small"
									onClick={() => onEdit(sectionKey, item)}
									sx={{ mr: 1 }}
									title="Edit">
									<EditIcon size={16} />
								</IconButton>
								{config.hasStatusToggle ? (
									// For visit_types, show activate/deactivate toggle
									<IconButton
										size="small"
										color={item.status ? "warning" : "success"}
										onClick={() =>
											onToggleStatus(sectionKey, item.id, !item.status)
										}
										title={item.status ? "Deactivate" : "Activate"}>
										{item.status ? (
											<DeactivateIcon size={16} />
										) : (
											<ActivateIcon size={16} />
										)}
									</IconButton>
								) : (
									// For other sections, show delete button
									<IconButton
										size="small"
										color="error"
										onClick={() => onDelete(sectionKey, item.id)}
										title="Delete">
										<TrashIcon size={16} />
									</IconButton>
								)}
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</TableContainer>

		{(!data[sectionKey] || data[sectionKey].length === 0) && (
			<Box sx={{ py: 4, textAlign: "center" }}>
				<Typography color="text.secondary">
					No items found in {config.title}
				</Typography>
			</Box>
		)}
	</Paper>
);

const MasterData = () => {
	const { supabase } = useAuth();
	const [data, setData] = useState({});
	const [openDialog, setOpenDialog] = useState(false);
	const [activeSection, setActiveSection] = useState(null);
	const [selectedItem, setSelectedItem] = useState(null);
	const [editForm, setEditForm] = useState({});
	const [successMessage, setSuccessMessage] = useState("");
	const [errorMessage, setErrorMessage] = useState("");
	const [isUploading, setIsUploading] = useState(false);
	const [uploadProgress, setUploadProgress] = useState(0);
	const fileInputRef = useRef(null);

	const thumbnailInputRef = useRef(null);

	const handleThumbnailUpload = async (event) => {
		const file = event.target.files[0];
		if (!file) return;

		try {
			setIsUploading(true);
			setUploadProgress(0);

			// Create a unique file name to avoid collisions
			const fileExt = file.name.split(".").pop();
			const fileName = `thumbnail_${Math.random()
				.toString(36)
				.substring(2, 15)}_${Date.now()}.${fileExt}`;
			const filePath = `${fileName}`;

			// Upload the file to Supabase Storage
			const { data, error } = await supabase.storage
				.from("onboardingvideos")
				.upload(filePath, file, {
					cacheControl: "3600",
					upsert: false,
					onUploadProgress: (progress) => {
						setUploadProgress(
							Math.round((progress.loaded / progress.total) * 100)
						);
					},
				});

			if (error) throw error;

			// Get the public URL for the uploaded file
			const {
				data: { publicUrl },
			} = supabase.storage.from("onboardingvideos").getPublicUrl(filePath);

			setEditForm((prevForm) => ({
				...prevForm,
				thumbnail: publicUrl,
			}));

			setSuccessMessage("Thumbnail uploaded successfully");
		} catch (err) {
			console.error("Error uploading thumbnail:", err);
			setErrorMessage("Error uploading thumbnail");
		} finally {
			setIsUploading(false);
			// Reset the file input
			if (thumbnailInputRef.current) {
				thumbnailInputRef.current.value = "";
			}
		}
	};

	const fetchData = async (section) => {
		try {
			const { data: sectionData, error } = await supabase
				.from(section)
				.select("*")
				.order(
					section === "onboarding_videos"
						? "playorder"
						: section === "video_quizzes"
						? "quiz_order" // Changed from display_time_percent to quiz_order
						: section === "funmath_topics"
						? "sort_order"
						: section === "visit_types"
						? "start_date"
						: "name"
				);

			if (error) throw error;
			setData((prev) => ({
				...prev,
				[section]: sectionData || [],
			}));
		} catch (err) {
			console.error(`Error fetching ${section}:`, err);
			setErrorMessage(`Error loading ${SECTIONS_CONFIG[section].title}`);
		}
	};

	useEffect(() => {
		// Fetch data for all sections
		Object.keys(SECTIONS_CONFIG).forEach((section) => {
			fetchData(section);
		});
	}, []);

	const handleAddNew = (section, initialValues = {}) => {
		setActiveSection(section);
		setSelectedItem(null);
		const newForm = { ...initialValues };

		// If adding a quiz, find the next available quiz order
		if (section === "video_quizzes" && newForm.video_id) {
			const existingQuizzes =
				data.video_quizzes?.filter((q) => q.video_id === newForm.video_id) ||
				[];

			// Determine next quiz order
			if (existingQuizzes.length > 0) {
				const maxOrder = Math.max(
					...existingQuizzes.map((q) => q.quiz_order || 0)
				);
				newForm.quiz_order = maxOrder + 1;
			} else {
				newForm.quiz_order = 1;
			}
		}

		SECTIONS_CONFIG[section].fields.forEach((field) => {
			if (!newForm[field.name]) {
				if (field.type === "number") {
					newForm[field.name] = field.min || 0;
				} else if (field.type === "select" && Array.isArray(field.options)) {
					newForm[field.name] = field.options[0];
				} else if (field.type !== "select") {
					newForm[field.name] = "";
				}
			}
		});

		// Set default visit_type for visit_types section
		if (section === "visit_types" && !newForm.visit_type) {
			newForm.visit_type = "Service";
		}

		// Set default status for visit_types section
		if (section === "visit_types" && newForm.status === undefined) {
			newForm.status = true;
		}

		if (section === "funmath_topics") {
			if (newForm.status === undefined) {
				newForm.status = true;
			}
			const list = data.funmath_topics || [];
			const max = list.length
				? Math.max(...list.map((t) => t.sort_order ?? 0))
				: 0;
			newForm.sort_order = max + 10;
		}

		setEditForm(newForm);
		setOpenDialog(true);
	};

	const handleEdit = (section, item) => {
		setActiveSection(section);
		setSelectedItem(item);
		const editedForm = { ...item };

		// Ensure all fields are set
		SECTIONS_CONFIG[section].fields.forEach((field) => {
			if (editedForm[field.name] === undefined) {
				if (field.type === "number") {
					editedForm[field.name] = field.min || 0;
				} else if (field.type === "select" && Array.isArray(field.options)) {
					editedForm[field.name] = field.options[0];
				} else if (field.type !== "select") {
					editedForm[field.name] = "";
				}
			}
		});

		// Ensure visit_type has a default value if not set
		if (section === "visit_types" && !editedForm.visit_type) {
			editedForm.visit_type = "Service";
		}

		setEditForm(editedForm);
		setOpenDialog(true);
	};

	const handleDelete = async (section, id) => {
		if (!window.confirm("Are you sure you want to delete this item?")) return;

		try {
			// If it's a video, we should also remove the file from storage
			if (section === "onboarding_videos") {
				const { data: videoData } = await supabase
					.from(section)
					.select("video_link")
					.eq("id", id)
					.single();

				if (videoData && videoData.video_link) {
					const filePath = videoData.video_link.split("/").pop();
					await supabase.storage.from("onboardingvideos").remove([filePath]);
				}
			}

			const { error } = await supabase.from(section).delete().eq("id", id);

			if (error) throw error;

			// If we deleted a quiz, we need to reorder the remaining quizzes
			if (section === "video_quizzes") {
				const deletedQuiz = data.video_quizzes.find((q) => q.id === id);
				if (deletedQuiz) {
					const videoQuizzes = data.video_quizzes.filter(
						(q) => q.video_id === deletedQuiz.video_id && q.id !== id
					);

					// Sort by existing quiz order
					videoQuizzes.sort(
						(a, b) => (a.quiz_order || 0) - (b.quiz_order || 0)
					);

					// Update quiz orders
					for (let i = 0; i < videoQuizzes.length; i++) {
						const quiz = videoQuizzes[i];
						const newOrder = i + 1;

						if (quiz.quiz_order !== newOrder) {
							await supabase
								.from("video_quizzes")
								.update({ quiz_order: newOrder })
								.eq("id", quiz.id);
						}
					}
				}
			}

			setSuccessMessage(
				`Item deleted successfully from ${SECTIONS_CONFIG[section].title}`
			);
			fetchData(section);

			setTimeout(() => {
				setSuccessMessage("");
			}, 3000);
		} catch (err) {
			console.error(`Error deleting from ${section}:`, err);
			setErrorMessage("Error deleting item");
		}
	};

	// New function to handle status toggle for visit_types
	const handleToggleStatus = async (section, id, newStatus) => {
		try {
			const { error } = await supabase
				.from(section)
				.update({ status: newStatus })
				.eq("id", id);

			if (error) throw error;

			setSuccessMessage(
				`Visit type ${newStatus ? "activated" : "deactivated"} successfully`
			);
			fetchData(section);

			setTimeout(() => {
				setSuccessMessage("");
			}, 3000);
		} catch (err) {
			console.error(`Error updating status for ${section}:`, err);
			setErrorMessage("Error updating status");
		}
	};

	const handleVideoUpload = async (event) => {
		const file = event.target.files[0];
		if (!file) return;

		try {
			setIsUploading(true);
			setUploadProgress(0);

			// Create a unique file name to avoid collisions
			const fileExt = file.name.split(".").pop();
			const fileName = `${Math.random()
				.toString(36)
				.substring(2, 15)}_${Date.now()}.${fileExt}`;
			const filePath = `${fileName}`;

			// Upload the file to Supabase Storage
			const { data, error } = await supabase.storage
				.from("onboardingvideos")
				.upload(filePath, file, {
					cacheControl: "3600",
					upsert: false,
					onUploadProgress: (progress) => {
						setUploadProgress(
							Math.round((progress.loaded / progress.total) * 100)
						);
					},
				});

			if (error) throw error;

			// Get the public URL for the uploaded file
			const {
				data: { publicUrl },
			} = supabase.storage.from("onboardingvideos").getPublicUrl(filePath);

			setEditForm((prevForm) => ({
				...prevForm,
				video_link: publicUrl,
			}));

			setSuccessMessage("Video uploaded successfully");
		} catch (err) {
			console.error("Error uploading video:", err);
			setErrorMessage("Error uploading video");
		} finally {
			setIsUploading(false);
			// Reset the file input
			if (fileInputRef.current) {
				fileInputRef.current.value = "";
			}
		}
	};

	const handleSave = async () => {
		try {
			const formToSave = { ...editForm };

			// For video quizzes, calculate the display_time_percent based on the quiz_order
			if (activeSection === "video_quizzes") {
				// Find total number of quizzes for this video
				const videoQuizzes =
					data.video_quizzes?.filter(
						(q) =>
							q.video_id === formToSave.video_id &&
							(selectedItem ? q.id !== selectedItem.id : true)
					) || [];

				// Add the current quiz being saved
				const totalQuizzes = videoQuizzes.length + 1;

				// Calculate the approximate percentage
				// We distribute quizzes evenly across the video (from 10% to 90%)
				const interval = 80 / totalQuizzes; // 80% is the range from 10% to 90%
				formToSave.display_time_percent = Math.round(
					10 + (formToSave.quiz_order - 1) * interval
				);

				// Ensure the percentage is within bounds
				formToSave.display_time_percent = Math.max(
					10,
					Math.min(90, formToSave.display_time_percent)
				);
			}

			if (selectedItem) {
				// Update
				const { error } = await supabase
					.from(activeSection)
					.update(formToSave)
					.eq("id", selectedItem.id);

				if (error) throw error;
				setSuccessMessage(
					`${SECTIONS_CONFIG[activeSection].title} updated successfully`
				);
			} else {
				// Insert
				const { error } = await supabase
					.from(activeSection)
					.insert([formToSave]);

				if (error) throw error;
				setSuccessMessage(
					`New ${SECTIONS_CONFIG[activeSection].title} added successfully`
				);
			}

			setOpenDialog(false);
			fetchData(activeSection);

			setTimeout(() => {
				setSuccessMessage("");
			}, 3000);
		} catch (err) {
			console.error(`Error saving ${activeSection}:`, err);
			setErrorMessage("Error saving item");
		}
	};

	const handleViewVideo = (videoUrl) => {
		window.open(videoUrl, "_blank");
	};

	const renderField = (field) => {
		if (field.type === "select") {
			if (typeof field.options === "string") {
				// This is a dropdown that gets values from another table
				return (
					<FormControl fullWidth sx={{ mt: 2 }}>
						<InputLabel id={`${field.name}-label`}>{field.label}</InputLabel>
						<Select
							labelId={`${field.name}-label`}
							value={editForm[field.name] || ""}
							onChange={(e) => {
								// If changing the video for a quiz, reset the quiz order
								const newValue = e.target.value;
								const newForm = {
									...editForm,
									[field.name]: newValue,
								};

								if (
									field.name === "video_id" &&
									activeSection === "video_quizzes"
								) {
									// Find the next available quiz order for this video
									const existingQuizzes =
										data.video_quizzes?.filter(
											(q) => q.video_id === newValue
										) || [];

									if (existingQuizzes.length > 0) {
										const maxOrder = Math.max(
											...existingQuizzes.map((q) => q.quiz_order || 0)
										);
										newForm.quiz_order = maxOrder + 1;
									} else {
										newForm.quiz_order = 1;
									}
								}

								setEditForm(newForm);
							}}
							label={field.label}>
							{data[field.options]?.map((option) => (
								<MenuItem key={option.id} value={option.id}>
									{option.title || option.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				);
			} else if (Array.isArray(field.options)) {
				// This is a dropdown with fixed options
				return (
					<FormControl fullWidth sx={{ mt: 2 }}>
						<InputLabel id={`${field.name}-label`}>{field.label}</InputLabel>
						<Select
							labelId={`${field.name}-label`}
							value={editForm[field.name] || ""}
							onChange={(e) =>
								setEditForm({
									...editForm,
									[field.name]: e.target.value,
								})
							}
							label={field.label}>
							{field.options.map((option) => (
								<MenuItem key={option} value={option}>
									{option}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				);
			}
		} else if (field.type === "date") {
			return (
				<TextField
					key={field.name}
					label={field.label}
					type="date"
					fullWidth
					value={editForm[field.name] || ""}
					onChange={(e) =>
						setEditForm({
							...editForm,
							[field.name]: e.target.value || null,
						})
					}
					sx={{ mt: 2 }}
					InputLabelProps={{ shrink: true }}
				/>
			);
		} else {
			// Regular text field or number field
			return (
				<TextField
					key={field.name}
					label={field.label}
					type={field.type}
					fullWidth
					value={editForm[field.name] || (field.type === "number" ? 0 : "")}
					onChange={(e) =>
						setEditForm({
							...editForm,
							[field.name]:
								field.type === "number"
									? parseInt(e.target.value, 10)
									: e.target.value,
						})
					}
					sx={{ mt: 2 }}
					InputProps={{
						readOnly: field.readonly,
						endAdornment:
							field.name === "video_link" && editForm.video_link ? (
								<IconButton
									edge="end"
									onClick={() => handleViewVideo(editForm.video_link)}>
									<LinkIcon size={16} />
								</IconButton>
							) : null,
						inputProps: {
							min: field.min,
							max: field.max,
						},
					}}
				/>
			);
		}
		if (field.name === "thumbnail" && editForm.thumbnail) {
			return (
				<TextField
					key={field.name}
					label={field.label}
					type={field.type}
					fullWidth
					value={editForm[field.name] || ""}
					onChange={(e) =>
						setEditForm({
							...editForm,
							[field.name]: e.target.value,
						})
					}
					sx={{ mt: 2 }}
					InputProps={{
						readOnly: field.readonly,
						endAdornment: (
							<IconButton
								edge="end"
								onClick={() => window.open(editForm.thumbnail, "_blank")}>
								<LinkIcon size={16} />
							</IconButton>
						),
					}}
				/>
			);
		}
	};

	return (
		<Box sx={{ p: 3 }}>
			{successMessage && (
				<Alert severity="success" sx={{ mb: 2 }}>
					{successMessage}
				</Alert>
			)}
			{errorMessage && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{errorMessage}
				</Alert>
			)}

			<Typography variant="h5" sx={{ mb: 3 }}>
				Manage Static Data
			</Typography>

			{activeSection === "video_quizzes" && (
				<Alert severity="info" sx={{ mb: 3 }}>
					Quiz times will be automatically calculated and distributed evenly
					throughout the video.
				</Alert>
			)}

			<Grid container spacing={3}>
				{Object.entries(SECTIONS_CONFIG).map(([key, config]) => (
					<Grid item xs={12} md={key === "video_quizzes" ? 12 : 6} key={key}>
						<DataSection
							sectionKey={key}
							config={config}
							onAdd={handleAddNew}
							onEdit={handleEdit}
							onDelete={handleDelete}
							onToggleStatus={handleToggleStatus}
							onViewVideo={handleViewVideo}
							data={data}
						/>
					</Grid>
				))}
			</Grid>

			<Dialog
				open={openDialog}
				onClose={() => setOpenDialog(false)}
				maxWidth="sm"
				fullWidth>
				<DialogTitle>
					{selectedItem
						? `Edit ${SECTIONS_CONFIG[activeSection]?.title}`
						: `Add New ${SECTIONS_CONFIG[activeSection]?.title}`}
				</DialogTitle>
				<DialogContent>
					{activeSection === "video_quizzes" && (
						<Alert severity="info" sx={{ mt: 2, mb: 1 }}>
							Quiz will be automatically timed based on its order. Lower order
							quizzes appear earlier in the video.
						</Alert>
					)}

					{activeSection &&
						SECTIONS_CONFIG[activeSection].fields.map((field) =>
							renderField(field)
						)}

					{activeSection && SECTIONS_CONFIG[activeSection].hasFileUpload && (
						<Box sx={{ mt: 3 }}>
							<Typography variant="subtitle2" sx={{ mb: 1 }}>
								Upload Video
							</Typography>
							<Box sx={{ display: "flex", alignItems: "center" }}>
								<Button
									variant="outlined"
									component="label"
									startIcon={<UploadIcon size={20} />}
									disabled={isUploading}
									sx={{ mr: 2 }}>
									{isUploading ? "Uploading..." : "Choose File"}
									<input
										type="file"
										hidden
										accept="video/*"
										onChange={handleVideoUpload}
										ref={fileInputRef}
									/>
								</Button>
								{isUploading && (
									<Box sx={{ display: "flex", alignItems: "center" }}>
										<CircularProgress size={24} sx={{ mr: 1 }} />
										<Typography variant="body2">{uploadProgress}%</Typography>
									</Box>
								)}
							</Box>
							{editForm.thumbnail && (
								<Box sx={{ mt: 2, display: "flex", alignItems: "center" }}>
									<img
										src={editForm.thumbnail}
										alt="Thumbnail preview"
										style={{
											width: "100px",
											height: "auto",
											marginRight: "10px",
										}}
									/>
									<IconButton
										onClick={() => setEditForm({ ...editForm, thumbnail: "" })}
										color="error"
										title="Remove thumbnail">
										<TrashIcon size={16} />
									</IconButton>
								</Box>
							)}
						</Box>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setOpenDialog(false)}>Cancel</Button>
					<Button onClick={handleSave} variant="contained">
						{selectedItem ? "Update" : "Add"}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default MasterData;
